// src/utils/Onboarding/autoMail.js
//
// Email trigger utilities for Firebase Cloud Functions.
//
// Three distinct email types:
//   1. sendWelcomeEmail       - Generic onboarding welcome via /sendAutoMail
//   2. sendAnalysisEmail      - Report-ready email via /sendEmail_ImageAnalysis_1
//   3. sendGenerationEmail    - Report-ready email via /sendEmail_ImageGeneration_1
//
// The report email endpoints (2 & 3) are hardcoded on the server side — no
// templateId is passed from the frontend. The server constructs the report link
// from requestId and injects it into the template automatically.

import { trackEvent } from '../Tracking';

// ========================================
// CONFIGURATION
// ========================================
const BASE_URL = 'https://europe-west3-match-medic-p0.cloudfunctions.net';
const SEND_AUTO_MAIL_ENDPOINT = `${BASE_URL}/sendAutoMail`;
const SEND_EMAIL_ANALYSIS_ENDPOINT = `${BASE_URL}/sendEmail_ImageAnalysis_1`;
const SEND_EMAIL_GENERATION_ENDPOINT = `${BASE_URL}/sendEmail_ImageGeneration_1`;

const FROM_EMAIL = 'alp@matchmedic.com';

// ========================================
// HELPER FUNCTIONS
// ========================================

/**
 * Extract first name from full name string
 */
function extractFirstName(fullName) {
  if (!fullName || typeof fullName !== 'string') return '';
  const trimmed = fullName.trim();
  if (!trimmed) return '';
  const firstName = trimmed.split(/\s+/)[0] || '';
  if (!firstName) return '';
  return firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase();
}

/**
 * Format display name with proper casing
 */
function formatDisplayName(fullName, maxChars = 30) {
  if (!fullName || typeof fullName !== 'string') return '';
  const titleCased = fullName
    .trim()
    .split(/\s+/)
    .map((part) =>
      part
        .split(/([-'\u2019\-])/)
        .map((token) => {
          if (token === '-' || token === "'" || token === '\u2019') return token;
          if (!token) return token;
          return token.charAt(0).toUpperCase() + token.slice(1).toLowerCase();
        })
        .join('')
    )
    .join(' ');
  if (titleCased.length > maxChars) return titleCased.slice(0, maxChars) + '.';
  return titleCased;
}

// ========================================
// WELCOME EMAIL (generic onboarding)
// ========================================

/**
 * Send automatic welcome email to user via Firebase Cloud Function.
 * This is a generic onboarding email — NOT a report link email.
 *
 * @param {Object} options
 * @param {string} options.email - Recipient email address (required)
 * @param {string} [options.fullName] - User's full name for personalization
 * @param {string} [options.sessionId] - Onboarding session ID for tracking
 * @param {string} [options.source] - Source identifier (e.g., 'image_analysis')
 * @param {number} [options.slideNumber] - Slide number where email was captured
 * @returns {Promise<{success: boolean, data?: any, error?: string}>}
 */
export async function sendWelcomeEmail({
  email,
  fullName = '',
  sessionId = null,
  source = 'unknown',
  slideNumber = null,
}) {
  if (!email || typeof email !== 'string') {
    console.error('[AutoMail] Invalid email provided:', email);
    return { success: false, error: 'Invalid email address' };
  }

  const cleanEmail = email.trim().toLowerCase();
  if (!cleanEmail) {
    console.error('[AutoMail] Empty email provided');
    return { success: false, error: 'Empty email address' };
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  if (!emailRegex.test(cleanEmail)) {
    console.error('[AutoMail] Invalid email format:', cleanEmail);
    return { success: false, error: 'Invalid email format' };
  }

  const firstName = extractFirstName(fullName);
  const displayName = formatDisplayName(fullName);

  const payload = {
    email: cleanEmail,
    autoMailType: 'welcome_email',
    templateId: 'd-bd06233e616d4d1ab716f6e730c3fe5c',
    fromEmail: FROM_EMAIL,
    userData: {
      firstName: firstName || 'there',
      displayName: displayName || cleanEmail.split('@')[0],
      email: cleanEmail,
    },
  };

  console.log('[AutoMail] Sending welcome email:', {
    email: cleanEmail,
    firstName,
    displayName,
    source,
    slideNumber,
    sessionId,
  });

  try {
    const response = await fetch(SEND_AUTO_MAIL_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      mode: 'cors',
      body: JSON.stringify(payload),
    });

    const result = await response.json();

    if (result.success) {
      console.log('[AutoMail] Welcome email sent successfully:', result);
      await trackEvent('welcome_email_sent', {
        onboarding_id: sessionId,
        email: cleanEmail,
        source,
        slide_number: slideNumber,
        first_name: firstName || null,
        display_name: displayName || null,
      });
      return { success: true, data: result.data };
    } else {
      console.error('[AutoMail] Failed to send welcome email:', result.message);
      await trackEvent('welcome_email_failed', {
        onboarding_id: sessionId,
        email: cleanEmail,
        source,
        slide_number: slideNumber,
        error_message: result.message || 'Unknown error',
      });
      return { success: false, error: result.message || 'Unknown error' };
    }
  } catch (error) {
    console.error('[AutoMail] Network/fetch error:', error);
    await trackEvent('welcome_email_error', {
      onboarding_id: sessionId,
      email: cleanEmail,
      source,
      slide_number: slideNumber,
      error_message: error.message || 'Network error',
      error_type: error.name || 'Error',
    });
    return { success: false, error: error.message || 'Network error' };
  }
}

// ========================================
// REPORT EMAIL FUNCTIONS
// ========================================

/**
 * Send analysis report-ready email via /sendEmail_ImageAnalysis_1.
 * Called when user submits email on ImageAnalysisSlide (step 5.5).
 *
 * The server constructs the report link automatically:
 *   matchmedic.com/view-report/{requestId}
 *
 * @param {Object} options
 * @param {string} options.email      - Recipient email address (required)
 * @param {string} options.requestId  - Analysis requestId from API response (required)
 * @param {string} options.password   - Password from API response (required)
 * @param {string} [options.userName] - User's name for personalization
 * @param {string} [options.sessionId]
 * @returns {Promise<{success: boolean, messageId?: string, followupsScheduled?: number, error?: string, duration?: string, timestamp?: string}>}
 */
export async function sendAnalysisEmail({ email, requestId, password, userName, sessionId }) {
  const startTime = Date.now();

  console.log('[AutoMail] sendAnalysisEmail called with:', {
    email: email ? `${email.slice(0, 6)}...` : null,
    requestId,
    hasPassword: !!password,
    userName,
  });

  if (!email || !requestId || !password) {
    console.error('[AutoMail] sendAnalysisEmail: missing required parameters:', {
      hasEmail: !!email,
      hasRequestId: !!requestId,
      hasPassword: !!password,
    });
    return {
      success: false,
      error: 'Missing required parameters',
      duration: `${Date.now() - startTime}ms`,
      timestamp: new Date().toISOString(),
    };
  }

  const cleanEmail = email.trim().toLowerCase();
  const firstName = extractFirstName(userName || '');

  // Payload matches /sendEmail_ImageAnalysis_1 contract exactly.
  // Server handles templateId, link construction, and follow-up scheduling.
  const payload = {
    targetEmail: cleanEmail,
    fromEmail: FROM_EMAIL,
    requestId,
    password,
    ...(firstName && { userName: firstName }),
  };

  try {
    const response = await fetch(SEND_EMAIL_ANALYSIS_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    const result = await response.json();
    const duration = `${Date.now() - startTime}ms`;

    if (result.success) {
      console.log('[AutoMail] Analysis report email sent successfully:', {
        triggerType: result.triggerType,
        messageId: result.messageId,
        followupsScheduled: result.followupsScheduled,
        duration,
      });

      trackEvent('email_analysis_report_triggered', {
        onboarding_id: sessionId || null,
        target_email: cleanEmail,
        request_id: requestId,
        message_id: result.messageId || null,
        followups_scheduled: result.followupsScheduled ?? null,
        duration,
      }).catch(console.error);

      return {
        success: true,
        messageId: result.messageId || null,
        followupsScheduled: result.followupsScheduled ?? null,
        duration,
        timestamp: result.timestamp || new Date().toISOString(),
      };
    } else {
      console.error('[AutoMail] Failed to send analysis report email:', result.error);

      trackEvent('email_analysis_report_failed', {
        onboarding_id: sessionId || null,
        target_email: cleanEmail,
        request_id: requestId,
        error_message: result.error || 'Unknown error',
        duration,
      }).catch(console.error);

      return {
        success: false,
        error: result.error || 'Unknown error',
        duration,
        timestamp: result.timestamp || new Date().toISOString(),
      };
    }
  } catch (error) {
    console.error('[AutoMail] sendAnalysisEmail network error:', error);
    const duration = `${Date.now() - startTime}ms`;

    trackEvent('email_analysis_report_error', {
      onboarding_id: sessionId || null,
      target_email: cleanEmail,
      request_id: requestId,
      error_message: error.message || 'Network error',
      duration,
    }).catch(console.error);

    return {
      success: false,
      error: error.message || 'Network error',
      duration,
      timestamp: new Date().toISOString(),
    };
  }
}

/**
 * Send generation report-ready email via /sendEmail_ImageGeneration_1.
 * Called when user submits email on BeforeAfterSlide (step 11).
 *
 * The server constructs the report link automatically:
 *   matchmedic.com/view-report/{requestId}?tab=generation
 *
 * @param {Object} options
 * @param {string} options.email      - Recipient email address (required)
 * @param {string} options.requestId  - Analysis requestId (used as the report key, required)
 * @param {string} options.password   - Password from API response (required)
 * @param {string} [options.userName] - User's name for personalization
 * @param {string} [options.sessionId]
 * @returns {Promise<{success: boolean, messageId?: string, followupsScheduled?: number, error?: string, duration?: string, timestamp?: string}>}
 */
export async function sendGenerationEmail({ email, requestId, password, userName, sessionId }) {
  const startTime = Date.now();

  console.log('[AutoMail] sendGenerationEmail called with:', {
    email: email ? `${email.slice(0, 6)}...` : null,
    requestId,
    hasPassword: !!password,
    userName,
  });

  if (!email || !requestId || !password) {
    console.error('[AutoMail] sendGenerationEmail: missing required parameters:', {
      hasEmail: !!email,
      hasRequestId: !!requestId,
      hasPassword: !!password,
    });
    return {
      success: false,
      error: 'Missing required parameters',
      duration: `${Date.now() - startTime}ms`,
      timestamp: new Date().toISOString(),
    };
  }

  const cleanEmail = email.trim().toLowerCase();
  const firstName = extractFirstName(userName || '');

  // Payload matches /sendEmail_ImageGeneration_1 contract exactly.
  // Server handles templateId, ?tab=generation link, and follow-up scheduling.
  const payload = {
    targetEmail: cleanEmail,
    fromEmail: FROM_EMAIL,
    requestId,
    password,
    ...(firstName && { userName: firstName }),
  };

  try {
    const response = await fetch(SEND_EMAIL_GENERATION_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    const result = await response.json();
    const duration = `${Date.now() - startTime}ms`;

    if (result.success) {
      console.log('[AutoMail] Generation report email sent successfully:', {
        triggerType: result.triggerType,
        messageId: result.messageId,
        followupsScheduled: result.followupsScheduled,
        duration,
      });

      trackEvent('email_generation_report_triggered', {
        onboarding_id: sessionId || null,
        target_email: cleanEmail,
        request_id: requestId,
        message_id: result.messageId || null,
        followups_scheduled: result.followupsScheduled ?? null,
        duration,
      }).catch(console.error);

      return {
        success: true,
        messageId: result.messageId || null,
        followupsScheduled: result.followupsScheduled ?? null,
        duration,
        timestamp: result.timestamp || new Date().toISOString(),
      };
    } else {
      console.error('[AutoMail] Failed to send generation report email:', result.error);

      trackEvent('email_generation_report_failed', {
        onboarding_id: sessionId || null,
        target_email: cleanEmail,
        request_id: requestId,
        error_message: result.error || 'Unknown error',
        duration,
      }).catch(console.error);

      return {
        success: false,
        error: result.error || 'Unknown error',
        duration,
        timestamp: result.timestamp || new Date().toISOString(),
      };
    }
  } catch (error) {
    console.error('[AutoMail] sendGenerationEmail network error:', error);
    const duration = `${Date.now() - startTime}ms`;

    trackEvent('email_generation_report_error', {
      onboarding_id: sessionId || null,
      target_email: cleanEmail,
      request_id: requestId,
      error_message: error.message || 'Network error',
      duration,
    }).catch(console.error);

    return {
      success: false,
      error: error.message || 'Network error',
      duration,
      timestamp: new Date().toISOString(),
    };
  }
}

// ========================================
// EXPORTS
// ========================================
export default {
  sendWelcomeEmail,
  sendAnalysisEmail,
  sendGenerationEmail,
  extractFirstName,
  formatDisplayName,
};
